// Public customer-facing ID Verification portal.
// URL: /verify/:token — accessible without login.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import {
  ShieldCheck, Camera, Upload, CheckCircle2, XCircle,
  User, IdCard, CreditCard, Loader2, AlertTriangle, MapPin, Search, BookMarked,
  PenLine, Lock,
} from 'lucide-react';
import SignaturePad from '../components/SignaturePad';
import { DateInput } from '../components/onboarding/FieldRenderer';
import { fmtDate } from '../components/onboarding/rules';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const STEP_CUSTOMER_DETAILS = 1;
const STEP_SELFIE = 2; // Now: video recording step (kept constant name for minimal diff)
const STEP_ID_CHOICE = 3;
const STEP_LICENCE_FRONT = 4;
const STEP_LICENCE_BACK = 5;
const STEP_PASSPORT = 6;
const STEP_MEDICARE = 7;
const STEP_DECLARATION = 8;
const STEP_REVIEW = 9;
const STEP_DONE = 10;

const IDVerifyCustomerPortal = () => {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [status, setStatus] = useState(null);

  const [step, setStep] = useState(STEP_CUSTOMER_DETAILS);
  const [form, setForm] = useState({
    first_name: '', last_name: '', date_of_birth: '', address: '',
    email: '', mobile: '',
  });
  const [idType, setIdType] = useState(null); // 'driver_licence' | 'passport'
  const [uploads, setUploads] = useState({
    selfie: null, recorded_video: null, driver_licence: null, driver_licence_back: null,
    passport: null, medicare_card: null,
  });
  // Which docs were pre-uploaded by the broker on the customer's behalf. When
  // `uploaded_by[docType] === 'broker'` we skip that upload step and show a
  // read-only "already provided by your broker" info card instead.
  const [brokerUploaded, setBrokerUploaded] = useState({
    selfie: false, recorded_video: false, driver_licence: false, driver_licence_back: false,
    passport: false, medicare_card: false,
  });
  const [brokerDocsApproved, setBrokerDocsApproved] = useState(false);
  const [uploading, setUploading] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [reference, setReference] = useState(null);
  const [consent, setConsent] = useState(false);
  const [declaration, setDeclaration] = useState(false);
  const [privacyAgreed, setPrivacyAgreed] = useState(false);
  const [signatureSavedAt, setSignatureSavedAt] = useState(null);
  const [savingSig, setSavingSig] = useState(false);
  const [geo, setGeo] = useState({ latitude: null, longitude: null });

  // ── SMS identity gate (Phase 4) ──
  const [verifyToken, setVerifyToken] = useState(() => { try { return sessionStorage.getItem(`idv-verify-${token}`) || ''; } catch { return ''; } });
  const [needVerify, setNeedVerify] = useState(false);
  const [phoneHint, setPhoneHint] = useState('');
  const [hasMobile, setHasMobile] = useState(true);
  const [gateSent, setGateSent] = useState(false);
  const [otpCode, setOtpCode] = useState('');
  const [gateBusy, setGateBusy] = useState(false);
  const [devCode, setDevCode] = useState(null);
  const [gateMobile, setGateMobile] = useState('');
  const clearVerify = useCallback(() => { try { sessionStorage.removeItem(`idv-verify-${token}`); } catch { /* ignore */ } setVerifyToken(''); setNeedVerify(true); setGateSent(false); }, [token]);
  useEffect(() => {
    if (verifyToken) axios.defaults.headers.common['X-IDV-Verify'] = verifyToken;
    else delete axios.defaults.headers.common['X-IDV-Verify'];
  }, [verifyToken]);
  useEffect(() => {
    const iid = axios.interceptors.response.use((r) => r, (err) => {
      if (err?.response?.status === 401 && String(err.config?.url || '').includes('/id-verify/public/')) {
        clearVerify(); toast.error('Your secure session expired — please verify by SMS again');
      }
      return Promise.reject(err);
    });
    return () => axios.interceptors.response.eject(iid);
  }, [clearVerify]);
  const requestOtp = async () => {
    setGateBusy(true);
    try {
      const { data } = await axios.post(`${API}/api/id-verify/public/${token}/request-otp`, gateMobile ? { mobile: gateMobile } : {});
      setGateSent(true); if (data.dev_code) setDevCode(data.dev_code); if (data.phone_hint) setPhoneHint(data.phone_hint);
      toast.success(data.delivered ? 'Code sent to your mobile' : 'Verification code generated');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not send code'); } finally { setGateBusy(false); }
  };
  const verifyOtp = async () => {
    if (otpCode.trim().length < 4) { toast.error('Enter the code we sent you'); return; }
    setGateBusy(true);
    try {
      const { data } = await axios.post(`${API}/api/id-verify/public/${token}/verify-otp`, { code: otpCode.trim() });
      try { sessionStorage.setItem(`idv-verify-${token}`, data.verify_token); } catch { /* ignore */ }
      axios.defaults.headers.common['X-IDV-Verify'] = data.verify_token;
      setVerifyToken(data.verify_token); setOtpCode(''); setDevCode(null); setNeedVerify(false);
      toast.success('Identity verified'); await loadStatus();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Verification failed'); } finally { setGateBusy(false); }
  };

  // Initial load — resolve token
  const loadStatus = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/id-verify/public/${token}`);
      setStatus(data);
      if (data.verification_required) {
        setNeedVerify(true); setPhoneHint(data.phone_hint || ''); setHasMobile(data.has_mobile !== false);
        if (['customer_submitted', 'broker_reviewed', 'report_generated'].includes(data.status)) { setStep(STEP_DONE); setReference(data.reference_number); }
        return;
      }
      setNeedVerify(false);
      if (data.status === 'customer_submitted' || data.status === 'broker_reviewed' || data.status === 'report_generated') {
        setStep(STEP_DONE);
        setReference(data.reference_number);
      }
      if (data.has_selfie) setUploads((u) => ({ ...u, selfie: { uploaded: true } }));
      if (data.has_recorded_video) setUploads((u) => ({ ...u, recorded_video: { uploaded: true } }));
      if (data.has_driver_licence) setUploads((u) => ({ ...u, driver_licence: { uploaded: true } }));
      if (data.has_driver_licence_back) setUploads((u) => ({ ...u, driver_licence_back: { uploaded: true } }));
      if (data.has_passport) setUploads((u) => ({ ...u, passport: { uploaded: true } }));
      if (data.has_medicare_card) setUploads((u) => ({ ...u, medicare_card: { uploaded: true } }));
      if (data.uploaded_by) {
        setBrokerUploaded({
          selfie: data.uploaded_by.selfie === 'broker',
          recorded_video: data.uploaded_by.recorded_video === 'broker',
          driver_licence: data.uploaded_by.driver_licence === 'broker',
          driver_licence_back: data.uploaded_by.driver_licence_back === 'broker',
          passport: data.uploaded_by.passport === 'broker',
          medicare_card: data.uploaded_by.medicare_card === 'broker',
        });
      }
      // Broker pre-fill — set every field on the form if broker populated them
      if (data.prefill) {
        setForm((f) => ({
          ...f,
          first_name: data.prefill.first_name || f.first_name,
          last_name: data.prefill.last_name || f.last_name,
          date_of_birth: data.prefill.date_of_birth || f.date_of_birth,
          address: data.prefill.address || f.address,
          email: data.prefill.email || f.email,
          mobile: data.prefill.mobile || f.mobile,
        }));
      }
      // Persist broker-docs approval flag so returning customers don't re-tick
      if (data.broker_docs_approved_at) setBrokerDocsApproved(true);
      if (data.id_type) setIdType(data.id_type);
      if (data.first_name) setForm((f) => ({ ...f, first_name: data.first_name }));
      if (data.last_name) setForm((f) => ({ ...f, last_name: data.last_name }));
    } catch (e) {
      const detail = e?.response?.data?.detail || 'Link not found';
      setError(detail);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  const uploadFile = async (fileType, file) => {
    if (!file) return;
    setUploading(fileType);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await axios.post(
        `${API}/api/id-verify/public/${token}/upload/${fileType}`,
        fd,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );
      setUploads((u) => {
        const next = { ...u, [fileType]: { uploaded: true, name: file.name } };
        // Backend clears the "other" ID pathway when the customer switches type — mirror locally
        if (fileType === 'passport') {
          next.driver_licence = null;
          next.driver_licence_back = null;
        } else if (fileType === 'driver_licence' || fileType === 'driver_licence_back') {
          next.passport = null;
        }
        return next;
      });
    } catch (e) {
      alert(e?.response?.data?.detail || 'Upload failed. Please try again.');
    } finally {
      setUploading('');
    }
  };

  // Try to get geolocation (optional, non-blocking)
  useEffect(() => {
    if (!('geolocation' in navigator)) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => setGeo({ latitude: pos.coords.latitude, longitude: pos.coords.longitude }),
      () => { /* user denied — leave null */ },
      { enableHighAccuracy: false, timeout: 8000, maximumAge: 60000 },
    );
  }, []);

  // Auto-skip any step whose doc has already been uploaded by the broker.
  // Runs whenever the customer changes step; short-circuits past pre-uploaded
  // slots so the flow feels seamless.
  useEffect(() => {
    if (step === STEP_SELFIE && (brokerUploaded.recorded_video || uploads.recorded_video?.uploaded)) {
      setStep(STEP_ID_CHOICE);
      return;
    }
    if (step === STEP_ID_CHOICE) {
      // If broker has provided a full photo ID, skip the choice entirely.
      if (brokerUploaded.passport) {
        setStep(brokerUploaded.medicare_card ? STEP_DECLARATION : STEP_MEDICARE);
        return;
      }
      if (brokerUploaded.driver_licence && brokerUploaded.driver_licence_back) {
        setStep(brokerUploaded.medicare_card ? STEP_DECLARATION : STEP_MEDICARE);
        return;
      }
      if (brokerUploaded.driver_licence && !brokerUploaded.driver_licence_back) {
        setStep(STEP_LICENCE_BACK);
        return;
      }
    }
    if (step === STEP_LICENCE_FRONT && brokerUploaded.driver_licence) {
      setStep(brokerUploaded.driver_licence_back ? (brokerUploaded.medicare_card ? STEP_DECLARATION : STEP_MEDICARE) : STEP_LICENCE_BACK);
      return;
    }
    if (step === STEP_LICENCE_BACK && brokerUploaded.driver_licence_back) {
      setStep(brokerUploaded.medicare_card ? STEP_DECLARATION : STEP_MEDICARE);
      return;
    }
    if (step === STEP_PASSPORT && brokerUploaded.passport) {
      setStep(brokerUploaded.medicare_card ? STEP_DECLARATION : STEP_MEDICARE);
      return;
    }
    if (step === STEP_MEDICARE && brokerUploaded.medicare_card) {
      setStep(STEP_DECLARATION);
    }
  }, [step, brokerUploaded]);

  // If broker has pre-uploaded BOTH sides of a licence OR a passport, auto-set
  // the id_type so the ID choice step + review page reflect it correctly.
  useEffect(() => {
    if (!idType) {
      if (brokerUploaded.passport) setIdType('passport');
      else if (brokerUploaded.driver_licence || brokerUploaded.driver_licence_back) setIdType('driver_licence');
    }
  }, [brokerUploaded, idType]);

  const uploadSignature = async (file) => {
    if (!file) return;
    setSavingSig(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await axios.post(
        `${API}/api/id-verify/public/${token}/signature`,
        fd,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );
      setSignatureSavedAt(data.uploaded_at);
    } catch (e) {
      alert(e?.response?.data?.detail || 'Signature save failed. Please try again.');
    } finally {
      setSavingSig(false);
    }
  };

  const submitAll = async () => {
    setSubmitting(true);
    try {
      const { data } = await axios.post(
        `${API}/api/id-verify/public/${token}/submit`,
        {
          ...form,
          consent_accepted: consent,
          declaration_accepted: declaration,
          privacy_accepted: privacyAgreed,
          latitude: geo.latitude,
          longitude: geo.longitude,
        },
      );
      setReference(data.reference_number);
      setStep(STEP_DONE);
    } catch (e) {
      alert(e?.response?.data?.detail || 'Submission failed. Please check all fields and try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const hasLicenceComplete = uploads.driver_licence?.uploaded && uploads.driver_licence_back?.uploaded;
  const hasPhotoID = hasLicenceComplete || uploads.passport?.uploaded;
  // If any doc was uploaded by the broker, customer must approve.
  const brokerUploadedAny = Object.values(brokerUploaded).some(Boolean);
  const canSubmit = uploads.recorded_video?.uploaded && uploads.medicare_card?.uploaded && hasPhotoID
    && declaration && privacyAgreed && consent && !!signatureSavedAt
    && (!brokerUploadedAny || brokerDocsApproved);

  const submitApproval = async () => {
    try {
      await axios.post(`${API}/api/id-verify/public/${token}/approve-broker-docs`, { approved: true });
      setBrokerDocsApproved(true);
    } catch (e) {
      alert(e?.response?.data?.detail || 'Approval failed');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#EEF1F6]">
        <Loader2 className="w-6 h-6 animate-spin text-slate-500" />
      </div>
    );
  }
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#EEF1F6] p-6">
        <div className="max-w-md w-full bg-white rounded-2xl border border-slate-200 shadow p-8 text-center">
          <XCircle className="w-12 h-12 text-red-500 mx-auto mb-3" />
          <h1 className="text-xl font-black text-slate-900 mb-1">Link unavailable</h1>
          <p className="text-sm text-slate-600">{error}</p>
          <p className="text-xs text-slate-500 mt-4">Please contact your broker for a new link.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#EEF1F6] text-slate-900 pb-20">
      {/* Header — matches the Client Portal */}
      <header className="bg-white/85 backdrop-blur-xl border-b border-slate-200/70 sticky top-0 z-40 shadow-sm">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 py-3 sm:py-3.5 flex items-center justify-between gap-3">
          {(() => { const b = status?.branding || {}; const isPrimary = !b.business_name || b.business_name === 'Derwent Finance'; return b.logo_url ? <img src={b.logo_url} alt={b.business_name} className="h-6 sm:h-8 w-auto object-contain" draggable={false} data-testid="idv-brand-logo" /> : isPrimary ? <img src="/derwent-finance-logo-dark.png" alt="Derwent Finance" className="h-6 sm:h-8 w-auto object-contain" draggable={false} /> : <span className="text-sm font-black text-slate-900" data-testid="idv-brand-name">{b.business_name}</span>; })()}
          <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-2.5 py-1 text-[10px] sm:text-[11px] font-bold text-emerald-700"><Lock className="h-3 w-3" /> Encrypted</span>
        </div>
      </header>

      {step === STEP_DONE ? (
        <DoneScreen reference={reference} />
      ) : (
        <div className="max-w-2xl mx-auto px-4 sm:px-6 py-5 sm:py-7">
          <div className="mb-5">
            <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-[#15133A]/60 flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5" /> Secure ID Verification</p>
            <h1 className="text-2xl sm:text-3xl font-black text-slate-950 mt-1 tracking-tight font-[Outfit]">Verify your identity</h1>
            <p className="text-sm text-slate-500 mt-1 leading-snug">Takes about 3 minutes. Your documents are encrypted and only shared with your broker.</p>
          </div>
          <StepIndicator step={step} idType={idType} />

          {needVerify ? (
            <div className="bg-white rounded-3xl border border-slate-200/80 shadow-[0_2px_16px_rgba(15,19,58,0.06)] p-6 sm:p-8 text-center" data-testid="idv-verify-gate">
              <div className="w-14 h-14 rounded-2xl bg-[#15133A]/10 mx-auto flex items-center justify-center mb-4"><ShieldCheck className="w-7 h-7 text-[#15133A]" /></div>
              <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-[#15133A]/60">Step 2 · Verify your mobile</p>
              <h2 className="text-xl sm:text-2xl font-black text-slate-950 mt-1 font-[Outfit]">Confirm it&apos;s you</h2>
              <p className="text-sm text-slate-600 mt-2 leading-relaxed max-w-sm mx-auto">For your security we&apos;ll text a one-time code{phoneHint ? <> to the mobile ending <span className="font-semibold">{phoneHint}</span></> : ''}. You&apos;ll need it before your details and documents can be accessed.</p>
              {!hasMobile && !gateSent && (
                <div className="mt-4 text-left max-w-sm mx-auto">
                  <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Your mobile number</label>
                  <input type="tel" inputMode="tel" value={gateMobile} onChange={(e) => setGateMobile(e.target.value)} data-testid="idv-verify-mobile" placeholder="04XX XXX XXX"
                    className="mt-1.5 w-full px-3 py-3 rounded-xl text-base outline-none bg-slate-50 border border-slate-200 focus:bg-white focus:border-slate-300 focus:ring-2 focus:ring-blue-100 min-h-[52px]" />
                </div>
              )}
              {!gateSent ? (
                <button type="button" onClick={requestOtp} disabled={gateBusy || (!hasMobile && !gateMobile.trim())} data-testid="idv-verify-send"
                  className="mt-6 w-full max-w-sm mx-auto flex items-center justify-center rounded-full bg-[#15133A] hover:bg-[#1E1B4B] text-white text-sm font-bold py-3.5 disabled:opacity-50 min-h-[52px]">
                  {gateBusy ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Send my verification code'}
                </button>
              ) : (
                <div className="mt-6 space-y-3 max-w-sm mx-auto text-left">
                  {devCode && <p className="text-[11px] text-center text-amber-700 bg-amber-50 border border-amber-200 rounded-lg py-1.5" data-testid="idv-verify-devcode">SMS not configured — test code: <span className="font-mono font-bold">{devCode}</span></p>}
                  <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500">Enter the 6-digit code</label>
                  <input type="text" inputMode="numeric" maxLength={6} value={otpCode} onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, ''))} data-testid="idv-verify-code"
                    className="w-full px-3 py-3 rounded-xl text-center text-2xl font-bold tracking-[0.4em] outline-none bg-slate-50 border border-slate-200 focus:bg-white focus:border-slate-300 focus:ring-2 focus:ring-blue-100 min-h-[56px]" placeholder="••••••" />
                  <button type="button" onClick={verifyOtp} disabled={gateBusy} data-testid="idv-verify-submit"
                    className="w-full flex items-center justify-center rounded-full bg-[#15133A] hover:bg-[#1E1B4B] text-white text-sm font-bold py-3.5 disabled:opacity-50 min-h-[52px]">
                    {gateBusy ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Verify & continue'}
                  </button>
                  <button type="button" onClick={requestOtp} disabled={gateBusy} data-testid="idv-verify-resend" className="w-full text-xs font-semibold text-slate-500 hover:text-slate-800 min-h-[36px]">Didn&apos;t get it? Resend code</button>
                </div>
              )}
              <p className="mt-5 text-[11px] text-slate-400">Codes expire after 5 minutes. You&apos;ll be asked to verify again if you return later.</p>
            </div>
          ) : (
          <>
          {step === STEP_CUSTOMER_DETAILS && (
            <StepCard title="Your details" subtitle="Enter your legal name, current residential address and contact details.">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <TextField label="First name" value={form.first_name} onChange={(v) => setForm({ ...form, first_name: v })} testId="idv-first-name" required />
                <TextField label="Last name" value={form.last_name} onChange={(v) => setForm({ ...form, last_name: v })} testId="idv-last-name" required />
              </div>
              <div>
                <span className="text-[11px] font-bold uppercase tracking-wider text-slate-600">Date of birth<span className="text-red-500 ml-0.5">*</span></span>
                <div className="mt-1">
                  <DateInput
                    field={{ key: 'date_of_birth', label: 'Date of birth' }}
                    value={form.date_of_birth}
                    onChange={(v) => setForm({ ...form, date_of_birth: v || '' })}
                    testId="idv-dob"
                  />
                </div>
              </div>
              <AddressAutocomplete
                label="Residential address"
                value={form.address}
                onChange={(v) => setForm({ ...form, address: v })}
                testId="idv-address"
                placeholder="Start typing your address…"
                required
              />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <TextField label="Email address" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} testId="idv-email" placeholder="[email protected]" required />
                <TextField label="Mobile number" type="tel" value={form.mobile} onChange={(v) => setForm({ ...form, mobile: v })} testId="idv-mobile" placeholder="04XX XXX XXX" />
              </div>
              <PrimaryButton
                onClick={() => setStep(brokerUploaded.recorded_video ? STEP_ID_CHOICE : STEP_SELFIE)}
                disabled={!(form.first_name && form.last_name && form.date_of_birth && form.address && form.email)}
                testId="idv-next-selfie"
              >
                {brokerUploaded.recorded_video ? 'Continue → Photo ID' : 'Continue → Record verification video'}
              </PrimaryButton>
            </StepCard>
          )}

          {step === STEP_SELFIE && (
            <VideoRecordStep
              uploaded={uploads.recorded_video?.uploaded}
              uploading={uploading === 'recorded_video'}
              onUpload={(f) => uploadFile('recorded_video', f)}
              onNext={() => setStep(STEP_ID_CHOICE)}
              onBack={() => setStep(STEP_CUSTOMER_DETAILS)}
            />
          )}

          {false && step === STEP_SELFIE && (
            <UploadStep
              title="Live selfie"
              subtitle="We'll compare this with your ID photo. Look directly at the camera in good light."
              icon={User}
              fileType="selfie"
              uploaded={uploads.selfie?.uploaded}
              uploading={uploading === 'selfie'}
              onUpload={(f) => uploadFile('selfie', f)}
              capture="user"
              onNext={() => setStep(STEP_ID_CHOICE)}
              onBack={() => setStep(STEP_CUSTOMER_DETAILS)}
              nextLabel="Next: Photo ID"
            />
          )}

          {step === STEP_ID_CHOICE && (
            <StepCard title="Choose your photo ID" subtitle="You must upload ONE of the two options below. Medicare card is still required in the next step regardless of which you pick.">
              <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 flex items-start gap-2 mb-3">
                <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                <p className="text-xs text-amber-900">
                  <b>Australian ID requirement:</b> we need either both sides of your driver licence <b>or</b> your passport — plus your Medicare card in the next step.
                </p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <IDChoiceCard
                  active={idType === 'driver_licence'}
                  icon={IdCard}
                  title="Driver licence"
                  subtitle="Requires both front AND back photos"
                  testId="idv-choose-driver_licence"
                  onClick={() => { setIdType('driver_licence'); setStep(STEP_LICENCE_FRONT); }}
                />
                <IDChoiceCard
                  active={idType === 'passport'}
                  icon={BookMarked}
                  title="Passport"
                  subtitle="Single photo of the ID page"
                  testId="idv-choose-passport"
                  onClick={() => { setIdType('passport'); setStep(STEP_PASSPORT); }}
                />
              </div>
              <div className="mt-4">
                <button
                  type="button"
                  onClick={() => setStep(STEP_SELFIE)}
                  className="w-full rounded-full border border-slate-300 hover:bg-slate-50 text-slate-800 text-sm font-semibold py-3"
                >
                  Back
                </button>
              </div>
            </StepCard>
          )}

          {step === STEP_LICENCE_FRONT && (
            <UploadStep
              title="Driver licence — FRONT"
              subtitle="Take a clear photo of the FRONT of your driver licence. Make sure all four corners and the text are readable."
              icon={IdCard}
              fileType="driver_licence"
              uploaded={uploads.driver_licence?.uploaded}
              uploading={uploading === 'driver_licence'}
              onUpload={(f) => uploadFile('driver_licence', f)}
              capture="environment"
              onNext={() => setStep(STEP_LICENCE_BACK)}
              onBack={() => setStep(STEP_ID_CHOICE)}
              nextLabel="Next: Licence back"
            />
          )}

          {step === STEP_LICENCE_BACK && (
            <UploadStep
              title="Driver licence — BACK"
              subtitle="Now take a photo of the BACK of the same licence. This is required — the front alone won't satisfy verification."
              icon={IdCard}
              fileType="driver_licence_back"
              uploaded={uploads.driver_licence_back?.uploaded}
              uploading={uploading === 'driver_licence_back'}
              onUpload={(f) => uploadFile('driver_licence_back', f)}
              capture="environment"
              onNext={() => setStep(STEP_MEDICARE)}
              onBack={() => setStep(STEP_LICENCE_FRONT)}
              nextLabel="Next: Medicare"
            />
          )}

          {step === STEP_PASSPORT && (
            <UploadStep
              title="Passport"
              subtitle="Photo of the identity page (the one with your photo, name and passport number). All four edges must be visible."
              icon={BookMarked}
              fileType="passport"
              uploaded={uploads.passport?.uploaded}
              uploading={uploading === 'passport'}
              onUpload={(f) => uploadFile('passport', f)}
              capture="environment"
              onNext={() => setStep(STEP_MEDICARE)}
              onBack={() => setStep(STEP_ID_CHOICE)}
              nextLabel="Next: Medicare"
            />
          )}

          {step === STEP_MEDICARE && (
            <UploadStep
              title="Medicare card"
              subtitle="Photo of the FRONT of your Medicare card. All names + the expiry date should be visible. This is required for everyone."
              icon={CreditCard}
              fileType="medicare_card"
              uploaded={uploads.medicare_card?.uploaded}
              uploading={uploading === 'medicare_card'}
              onUpload={(f) => uploadFile('medicare_card', f)}
              capture="environment"
              onNext={() => setStep(STEP_DECLARATION)}
              onBack={() => setStep(idType === 'passport' ? STEP_PASSPORT : STEP_LICENCE_BACK)}
              nextLabel="Next: Sign"
            />
          )}

          {step === STEP_DECLARATION && (
            <StepCard title="Declaration & Privacy Consent" subtitle="Please read carefully — sign at the bottom to continue.">
              {/* Section 1: Identity declaration */}
              <div className="rounded-xl border border-slate-200 bg-white p-4">
                <p className="text-xs font-bold uppercase tracking-wider text-slate-600 mb-2">Declaration — Individual Identified</p>
                <p className="text-sm text-slate-800 leading-relaxed">
                  I, <b>{fullName(form) || '[your full name]'}</b>, hereby confirm that:
                </p>
                <ul className="text-sm text-slate-700 leading-relaxed mt-2 space-y-1 ml-4 list-[lower-alpha]">
                  <li>The identification documents provided relate to me;</li>
                  <li>I am supplying my identity documents on <b>{new Date().toLocaleDateString('en-AU', { day: '2-digit', month: 'long', year: 'numeric' })}</b>;</li>
                  <li>I provided digital copies of my original identification documents, being <b>{idDocsList(uploads, idType)}</b>; and</li>
                  <li>Where applicable, I confirm that I am authorised to provide the personal details presented and I consent to my information being checked with the document issuer or official record holder via third-party systems for the purpose of confirming my identity.</li>
                </ul>
                <label className="mt-3 flex items-start gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={declaration}
                    onChange={(e) => setDeclaration(e.target.checked)}
                    data-testid="idv-declaration-checkbox"
                    className="mt-0.5 w-4 h-4 rounded border-slate-300 text-slate-900 focus:ring-slate-900/20"
                  />
                  <span className="text-xs text-slate-800">I confirm the declaration above.</span>
                </label>
              </div>

              {/* Section 2: Privacy statement — displayed in full, no nested scroll */}
              <div className="rounded-xl border border-slate-200 bg-white p-4">
                <p className="text-xs font-bold uppercase tracking-wider text-slate-600 mb-2">Privacy Statement & Third-Party Consent</p>
                <div className="text-xs text-slate-700 leading-relaxed space-y-2">
                  <p><b>Our Privacy Commitment.</b> Privacy matters to us and we know it matters to you. We are committed to protecting your privacy, keeping your information safe and ensuring the security of your data.</p>
                  <p><b>What we collect.</b> By completing this Verification of Identity, you are providing your personal information to the person performing the verification and to the party that requires your identity verified (the Receiver). This information is collected for the primary purpose of assessing your application, verifying your identity, assessing you for any of the Receiver&apos;s products and services, and establishing and administering your account with the Receiver.</p>
                  <p><b>How we store it.</b> We store your information in electronic format in secure cloud storage facilities we operate ourselves or that are operated by our service providers. We use technical solutions, security controls and internal processes to protect your information from unauthorised access, misuse, interference, loss and disclosure.</p>
                  <p><b>How we use it.</b> Administration and compliance — including identity verification, fraud prevention, credit assessment, and any purpose required or authorised by law.</p>
                  <p><b>Who we share it with.</b> Authorised representatives, credit providers, credit reporting bodies, fraud-checking agencies, third-party verification providers, lenders, funders, aggregators and law enforcement/government bodies where required or authorised by law.</p>
                  <p><b>Customer Consent.</b> I, <b>{fullName(form) || '[your full name]'}</b>, hereby consent that my personal information may be used and disclosed to the Receiver and any of their third-party service providers for the purposes outlined above. I acknowledge that I have read, understood and agree to the declaration, privacy statement and third-party consent above.</p>
                </div>
                <label className="mt-3 flex items-start gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={privacyAgreed}
                    onChange={(e) => setPrivacyAgreed(e.target.checked)}
                    data-testid="idv-privacy-checkbox"
                    className="mt-0.5 w-4 h-4 rounded border-slate-300 text-slate-900 focus:ring-slate-900/20"
                  />
                  <span className="text-xs text-slate-800">I have read and agree to the Customer Privacy Statement &amp; Third-Party Consent.</span>
                </label>
              </div>

              {/* Section 3: Signature pad */}
              <div>
                <p className="text-xs font-bold uppercase tracking-wider text-slate-600 mb-2 flex items-center gap-1.5">
                  <PenLine className="w-3.5 h-3.5" /> Your digital signature
                </p>
                <SignaturePad
                  onSaved={uploadSignature}
                  saving={savingSig}
                  savedAt={signatureSavedAt}
                  testIdPrefix="idv-signature"
                />
                <p className="text-[11px] text-slate-500 mt-1">
                  Your signature, IP address, device and (if permitted) GPS coordinates are recorded with a timestamp.
                  {geo.latitude && (
                    <span className="ml-1 text-emerald-600 font-semibold">GPS captured ✓</span>
                  )}
                </p>
              </div>

              <div className="grid grid-cols-2 gap-3 mt-2">
                <button
                  type="button"
                  onClick={() => setStep(STEP_MEDICARE)}
                  className="w-full rounded-full border border-slate-300 hover:bg-slate-50 text-slate-800 text-sm font-semibold py-3"
                >
                  Back
                </button>
                <button
                  type="button"
                  onClick={() => setStep(STEP_REVIEW)}
                  disabled={!declaration || !privacyAgreed || !signatureSavedAt}
                  data-testid="idv-declaration-next"
                  className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#15133A] hover:bg-[#1E1B4B] text-white text-sm font-semibold py-3 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Continue → Review
                </button>
              </div>
            </StepCard>
          )}

          {step === STEP_REVIEW && (
            <StepCard title="Review & submit" subtitle="Please double-check your details. Once submitted, you can't edit.">
              <ReviewList form={form} uploads={uploads} idType={idType} signatureSavedAt={signatureSavedAt} />
              {!hasPhotoID || !uploads.selfie?.uploaded || !uploads.medicare_card?.uploaded || !signatureSavedAt || !declaration || !privacyAgreed ? (
                <div className="rounded-xl border border-red-200 bg-red-50 p-3 flex items-start gap-2 mt-3">
                  <AlertTriangle className="w-4 h-4 text-red-600 shrink-0 mt-0.5" />
                  <p className="text-xs text-red-900">
                    You still need: selfie + Medicare card + <b>either</b> both sides of your driver licence <b>or</b> your passport + declaration + privacy consent + signature before you can submit.
                  </p>
                </div>
              ) : null}

              {/* Broker-uploaded docs approval — required if broker uploaded any doc */}
              {brokerUploadedAny && (
                <label className={`mt-4 flex items-start gap-3 rounded-xl border-2 p-3 cursor-pointer transition-colors ${
                  brokerDocsApproved ? 'border-emerald-300 bg-emerald-50' : 'border-amber-300 bg-amber-50 idv-btn-nudge'
                }`} data-testid="idv-broker-docs-approval">
                  <input
                    type="checkbox"
                    checked={brokerDocsApproved}
                    onChange={(e) => e.target.checked && submitApproval()}
                    data-testid="idv-broker-docs-approve-checkbox"
                    className="mt-0.5 w-4 h-4 rounded border-slate-400 text-emerald-600 focus:ring-emerald-500"
                  />
                  <span className="text-xs text-slate-800 leading-relaxed">
                    <b className="text-slate-900">I confirm</b> that the identity documents already uploaded by my broker on my behalf ({[
                      brokerUploaded.selfie && 'live selfie',
                      brokerUploaded.driver_licence && 'driver licence front',
                      brokerUploaded.driver_licence_back && 'driver licence back',
                      brokerUploaded.passport && 'passport',
                      brokerUploaded.medicare_card && 'Medicare card',
                    ].filter(Boolean).join(', ')}) are my genuine documents and I approve their use for this verification.
                  </span>
                </label>
              )}

              {/* Consent statement — required before submission (Aus Privacy Act / OAIC compliance) */}
              <label className="mt-4 flex items-start gap-3 rounded-xl border border-slate-200 bg-white p-3 cursor-pointer hover:bg-slate-50 transition-colors" data-testid="idv-consent-block">
                <input
                  type="checkbox"
                  checked={consent}
                  onChange={(e) => setConsent(e.target.checked)}
                  data-testid="idv-consent-checkbox"
                  className="mt-0.5 w-4 h-4 rounded border-slate-300 text-slate-900 focus:ring-slate-900/20"
                />
                <span className="text-xs text-slate-700 leading-relaxed">
                  <b className="text-slate-900">I confirm</b> everything above is accurate and I&apos;m ready to submit. My signature, timestamp and IP address will be recorded against this verification for compliance purposes.
                </span>
              </label>

              <div className="grid grid-cols-2 gap-2 sm:gap-3 mt-4">
                <button
                  type="button"
                  onClick={() => setStep(STEP_DECLARATION)}
                  className="w-full rounded-full border border-slate-300 hover:bg-slate-50 text-slate-800 text-xs sm:text-sm font-semibold py-2.5 sm:py-3 leading-tight"
                >
                  Back
                </button>
                <button
                  type="button"
                  onClick={submitAll}
                  disabled={submitting || !canSubmit}
                  data-testid="idv-submit"
                  className={`w-full inline-flex items-center justify-center gap-1.5 rounded-full text-xs sm:text-sm font-semibold py-2.5 sm:py-3 leading-tight ${
                    (submitting || !canSubmit)
                      ? 'bg-slate-200 text-slate-500 cursor-not-allowed'
                      : 'bg-[#15133A] hover:bg-[#1E1B4B] text-white idv-btn-nudge'
                  }`}
                >
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                  Submit verification
                </button>
              </div>
            </StepCard>
          )}
          </>
          )}
        </div>
      )}
    </div>
  );
};

const StepIndicator = ({ step, idType }) => {
  // 6 stages visible to the customer regardless of ID path
  const stageOfStep = (s) => {
    if (s === STEP_CUSTOMER_DETAILS) return 1;
    if (s === STEP_SELFIE) return 2;
    if (s === STEP_ID_CHOICE || s === STEP_LICENCE_FRONT || s === STEP_LICENCE_BACK || s === STEP_PASSPORT) return 3;
    if (s === STEP_MEDICARE) return 4;
    if (s === STEP_DECLARATION) return 5;
    if (s === STEP_REVIEW) return 6;
    return 6;
  };
  const idLabel = idType === 'passport' ? 'Passport' : idType === 'driver_licence' ? 'Licence' : 'Photo ID';
  const labels = ['Details', 'Video', idLabel, 'Medicare', 'Sign', 'Review'];
  const cur = stageOfStep(step);
  return (
    <div className="mb-5" role="progressbar" aria-valuenow={cur} aria-valuemax={6}>
      {/* Mobile: compact step marker + animated fill */}
      <div className="sm:hidden mb-2">
        <div className="flex items-baseline justify-between">
          <p className="text-[13px] font-black text-slate-900 font-[Outfit]">Step {cur} of 6 · {labels[cur - 1]}</p>
          <p className="text-[11px] font-bold text-slate-400">{Math.round((cur / 6) * 100)}%</p>
        </div>
        <div className="mt-1.5 h-1.5 rounded-full bg-slate-200 overflow-hidden">
          <div className="h-full rounded-full bg-[#15133A] transition-all duration-500" style={{ width: `${(cur / 6) * 100}%` }} />
        </div>
      </div>
      {/* Desktop: labelled stepper */}
      <div className="hidden sm:flex items-center gap-2">
        {labels.map((l, i) => {
          const s = i + 1;
          const active = cur === s;
          const done = cur > s;
          return (
            <div key={l} className="flex-1 flex items-center gap-2">
              <div className={`w-7 h-7 rounded-full text-[11px] font-bold flex items-center justify-center shrink-0 transition-colors ${
                done ? 'bg-emerald-500 text-white' : active ? 'bg-[#15133A] text-white' : 'bg-white text-slate-500 border border-slate-200'
              }`}>
                {done ? <CheckCircle2 className="w-3.5 h-3.5" /> : s}
              </div>
              <div className={`text-[11px] font-semibold ${active ? 'text-slate-900' : done ? 'text-emerald-700' : 'text-slate-500'} truncate`}>{l}</div>
              {i < labels.length - 1 && <div className={`h-0.5 flex-1 rounded-full ${done ? 'bg-emerald-400' : 'bg-slate-200'}`} />}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Selectable card used on the ID Choice step
const IDChoiceCard = ({ active, icon: Icon, title, subtitle, onClick, testId }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testId}
    className={`text-left rounded-2xl border-2 p-4 transition-all ${
      active
        ? 'border-slate-900 bg-slate-50 shadow-sm'
        : 'border-slate-200 bg-white hover:border-slate-400'
    }`}
  >
    <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center mb-3">
      <Icon className="w-5 h-5 text-slate-700" />
    </div>
    <p className="text-sm font-black text-slate-900">{title}</p>
    <p className="text-xs text-slate-500 mt-1">{subtitle}</p>
    {active && (
      <p className="text-[11px] font-bold uppercase tracking-wider text-emerald-600 mt-2 inline-flex items-center gap-1">
        <CheckCircle2 className="w-3 h-3" /> Selected
      </p>
    )}
  </button>
);

const StepCard = ({ title, subtitle, children }) => (
  <div className="bg-white rounded-3xl border border-slate-200/80 shadow-[0_2px_12px_rgba(15,19,58,0.05)] p-4 sm:p-6 space-y-4">
    <div>
      <h2 className="text-lg sm:text-xl font-black text-slate-950 leading-tight tracking-tight font-[Outfit]">{title}</h2>
      {subtitle && <p className="text-[13px] sm:text-sm text-slate-500 mt-1 leading-snug">{subtitle}</p>}
    </div>
    {children}
  </div>
);

const TextField = ({ label, value, onChange, type = 'text', placeholder, required, testId }) => (
  <label className="block">
    <span className="text-[11px] font-bold uppercase tracking-wider text-slate-600">
      {label}{required && <span className="text-red-500 ml-0.5">*</span>}
    </span>
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      data-testid={testId}
      className="mt-1 w-full px-3 py-2.5 rounded-lg border border-slate-200 text-base sm:text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-900/10 focus:border-slate-400"
    />
  </label>
);

// Address autocomplete backed by Mapbox Geocoding v6 (uses REACT_APP_MAPBOX_TOKEN).
// Debounced 300ms, results filtered to Australia via country=au.
const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN;
const AddressAutocomplete = ({ label, value, onChange, required, testId, placeholder }) => {
  const [query, setQuery] = useState(value || '');
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef(null);
  const wrapRef = useRef(null);
  const sessionRef = useRef(Math.random().toString(36).slice(2));
  const justPickedRef = useRef(false); // suppresses one fetch cycle after a pick

  // Sync external prefill (e.g. server-side prefill arrives after mount) into
  // the local input WITHOUT stomping on the user's active typing.
  useEffect(() => {
    if (value && !query) setQuery(value);
  }, [value]);

  // Close suggestions on outside click OR Escape
  useEffect(() => {
    const onDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, []);

  // Debounced fetch to Mapbox
  useEffect(() => {
    if (!MAPBOX_TOKEN) return;
    // If we've JUST picked a suggestion, don't refetch for the picked address
    if (justPickedRef.current) {
      justPickedRef.current = false;
      return;
    }
    if (!query || query.length < 3) {
      setSuggestions([]);
      setOpen(false);
      return;
    }
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const url = 'https://api.mapbox.com/search/searchbox/v1/suggest';
        const resp = await axios.get(url, {
          params: {
            q: query,
            country: 'au',
            language: 'en',
            limit: 6,
            session_token: sessionRef.current,
            access_token: MAPBOX_TOKEN,
            types: 'address,street,place,postcode,locality,neighborhood',
          },
        });
        // Bail if a pick raced in while the request was in-flight
        if (justPickedRef.current) return;
        const items = Array.isArray(resp.data?.suggestions) ? resp.data.suggestions : [];
        setSuggestions(items);
        setOpen(items.length > 0);
      } catch {
        setSuggestions([]);
      } finally {
        setLoading(false);
      }
    }, 300);
    return () => clearTimeout(debounceRef.current);
  }, [query]);

  const pick = async (item) => {
    // Immediately lock the UI so the fetch effect doesn't reopen the dropdown
    justPickedRef.current = true;
    setOpen(false);
    setSuggestions([]);
    setLoading(false);

    let full = item.full_address || item.place_formatted || item.name || '';
    try {
      const resp = await axios.get(`https://api.mapbox.com/search/searchbox/v1/retrieve/${item.mapbox_id}`, {
        params: { session_token: sessionRef.current, access_token: MAPBOX_TOKEN },
      });
      const feature = resp.data?.features?.[0];
      if (feature) {
        full = feature.properties?.full_address || feature.properties?.place_formatted || full;
      }
    } catch { /* fall back to suggestion text */ }
    setQuery(full);
    onChange(full);
    // rotate session for next search (Mapbox billing best-practice)
    sessionRef.current = Math.random().toString(36).slice(2);
  };

  return (
    <label className="block relative" ref={wrapRef}>
      <span className="text-[11px] font-bold uppercase tracking-wider text-slate-600">
        {label}{required && <span className="text-red-500 ml-0.5">*</span>}
      </span>
      <div className="relative">
        {loading ? (
          <Loader2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 animate-spin" />
        ) : (
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        )}
        <input
          type="text"
          value={query}
          onChange={(e) => { setQuery(e.target.value); onChange(e.target.value); }}
          onFocus={() => suggestions.length > 0 && setOpen(true)}
          placeholder={placeholder}
          data-testid={testId}
          autoComplete="off"
          className="mt-1 w-full pl-9 pr-3 py-2.5 rounded-lg border border-slate-200 text-base sm:text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-900/10 focus:border-slate-400"
        />
      </div>
      {open && suggestions.length > 0 && (
        <div className="absolute z-20 left-0 right-0 mt-1 rounded-lg border border-slate-200 bg-white shadow-lg overflow-hidden" data-testid="idv-address-suggestions">
          {suggestions.map((s) => (
            <button
              key={s.mapbox_id}
              type="button"
              onClick={() => pick(s)}
              className="w-full text-left px-3 py-2 hover:bg-slate-50 text-xs text-slate-800 border-b border-slate-100 last:border-0 flex items-start gap-2"
            >
              <MapPin className="w-3.5 h-3.5 text-slate-400 mt-0.5 shrink-0" />
              <span className="line-clamp-2">
                <b className="text-slate-900">{s.name}</b>
                {s.place_formatted && <span className="text-slate-500"> — {s.place_formatted}</span>}
              </span>
            </button>
          ))}
        </div>
      )}
      <p className="text-[10px] text-slate-400 mt-1">
        {MAPBOX_TOKEN ? 'Start typing — Australian address suggestions appear as you go.' : 'Type your full residential address.'}
      </p>
    </label>
  );
};

const PrimaryButton = ({ onClick, disabled, children, testId }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    data-testid={testId}
    className={`w-full mt-2 inline-flex items-center justify-center gap-2 rounded-full text-xs sm:text-sm font-semibold py-2.5 sm:py-3 leading-tight ${
      disabled
        ? 'bg-slate-200 text-slate-500 cursor-not-allowed'
        : 'bg-[#15133A] hover:bg-[#1E1B4B] text-white idv-btn-nudge'
    }`}
  >
    {children}
  </button>
);

// Human-friendly document banners — each step forces the customer to pause,
// read the exact document needed, tick a confirmation, and only then can
// they open the file picker. Prevents wrong-file uploads.
const DOC_META = {
  selfie: {
    label: 'Live selfie of YOUR face',
    checklist: [
      'Only YOUR face — no one else in the frame',
      'Good lighting, no sunglasses, hat off',
      'Look directly at the camera',
    ],
  },
  driver_licence: {
    label: 'FRONT of your Driver Licence',
    checklist: [
      'The FRONT (photo side) — not the back',
      'All 4 corners visible in the photo',
      'Name, DOB and licence number must be readable',
      'No glare or blur',
    ],
  },
  driver_licence_back: {
    label: 'BACK of your Driver Licence',
    checklist: [
      'The BACK (address/barcode side) — not the front',
      'All 4 corners visible',
      'Address and card number must be readable',
    ],
  },
  passport: {
    label: 'Photo/Identity page of your Passport',
    checklist: [
      'The page with YOUR photo, name and passport number',
      'All 4 edges visible in the photo',
      'The machine-readable text at the bottom must be readable',
    ],
  },
  medicare_card: {
    label: 'FRONT of your Medicare card',
    checklist: [
      'The FRONT of the card',
      'All names on the card must be visible',
      'The expiry date must be readable',
      'All 4 corners visible',
    ],
  },
};


// ─── VideoRecordStep — customer records themselves holding ID next to face ─
const REC_MIN_SECONDS = 5;
const REC_MAX_SECONDS = 20;
const REC_MIME_CANDIDATES = [
  'video/mp4;codecs=avc1,mp4a',
  'video/mp4',
  'video/webm;codecs=vp9,opus',
  'video/webm;codecs=vp8,opus',
  'video/webm',
];

const pickRecMime = () => {
  if (typeof MediaRecorder === 'undefined') return null;
  for (const m of REC_MIME_CANDIDATES) {
    try { if (MediaRecorder.isTypeSupported(m)) return m; } catch (_) { /* noop */ }
  }
  return '';
};

const VideoRecordStep = ({ uploaded, uploading, onUpload, onNext, onBack }) => {
  const videoRef = useRef(null);
  const previewRef = useRef(null);
  const streamRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const fallbackInputRef = useRef(null);

  const [permissionState, setPermissionState] = useState('idle'); // idle | granting | granted | denied
  const [isRecording, setIsRecording] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewBlob, setPreviewBlob] = useState(null);
  const [previewFile, setPreviewFile] = useState(null);
  const [previewError, setPreviewError] = useState(false);
  const [supported, setSupported] = useState(true);

  // Detect MediaRecorder support once
  useEffect(() => {
    const ok = typeof MediaRecorder !== 'undefined' &&
               typeof navigator !== 'undefined' &&
               !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia);
    setSupported(ok);
  }, []);

  const startCamera = useCallback(async () => {
    setPermissionState('granting');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user', width: { ideal: 720 }, height: { ideal: 1280 } },
        audio: true,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.muted = true;
        try { await videoRef.current.play(); } catch (_) { /* Autoplay quirks — ignore */ }
      }
      setPermissionState('granted');
    } catch (err) {
      setPermissionState('denied');
      alert('We need access to your camera and microphone to record your verification video. Please allow permissions and try again.');
    }
  }, []);

  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
  }, []);

  useEffect(() => stopCamera, [stopCamera]);

  const startRecording = () => {
    if (!streamRef.current) return;
    chunksRef.current = [];
    const mime = pickRecMime();
    let mr;
    try {
      mr = new MediaRecorder(streamRef.current, mime ? { mimeType: mime } : undefined);
    } catch (_e) {
      alert('Your browser does not support in-browser video recording. Please try a modern browser (Chrome / Safari / Edge).');
      return;
    }
    mediaRecorderRef.current = mr;
    mr.ondataavailable = (e) => { if (e.data && e.data.size > 0) chunksRef.current.push(e.data); };
    mr.onstop = () => {
      const blob = new Blob(chunksRef.current, { type: mr.mimeType || 'video/webm' });
      const url = URL.createObjectURL(blob);
      setPreviewBlob(blob);
      setPreviewUrl(url);
      setPreviewError(false);
      const ext = (blob.type.includes('mp4') ? 'mp4' : 'webm');
      const file = new File([blob], `verification-video.${ext}`, { type: blob.type });
      setPreviewFile(file);
      setIsRecording(false);
      stopCamera();
    };
    mr.start();
    setIsRecording(true);
    setElapsed(0);
    timerRef.current = setInterval(() => {
      setElapsed((s) => {
        const next = s + 1;
        if (next >= REC_MAX_SECONDS) { try { mr.stop(); } catch (_) { /* noop */ } }
        return next;
      });
    }, 1000);
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      try { mediaRecorderRef.current.stop(); } catch (_) { /* noop */ }
    }
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
  };

  const retake = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
    setPreviewBlob(null);
    setPreviewFile(null);
    setPreviewError(false);
    setElapsed(0);
    startCamera();
  };

  // Native file-input fallback: some browsers (older iOS, in-app WebViews) block
  // MediaRecorder but still allow the OS camera picker via <input capture>.
  const handleFallbackFile = (e) => {
    const f = e.target.files && e.target.files[0];
    if (!f) return;
    if (!f.type.startsWith('video/')) {
      alert('Please choose a video file.');
      return;
    }
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    const url = URL.createObjectURL(f);
    setPreviewBlob(f);
    setPreviewUrl(url);
    setPreviewError(false);
    setPreviewFile(f);
    if (e.target) e.target.value = '';
  };

  const submit = async () => {
    if (!previewFile) return;
    await onUpload(previewFile);
  };

  if (uploaded) {
    return (
      <StepCard title="Video recording complete" subtitle="Thanks — your verification video is saved. Continue to the next step.">
        <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-4 flex items-center gap-3 mb-4" data-testid="idv-video-uploaded">
          <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0" />
          <p className="text-sm text-slate-800">
            Your verification video has been uploaded successfully.
          </p>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <button type="button" onClick={onBack} className="rounded-full border border-slate-300 hover:bg-slate-50 text-slate-800 text-sm font-semibold py-3">Back</button>
          <button type="button" onClick={onNext} data-testid="idv-video-next" className="rounded-full bg-[#15133A] hover:bg-[#1E1B4B] text-white text-sm font-semibold py-3">Next: Photo ID</button>
        </div>
      </StepCard>
    );
  }

  return (
    <StepCard
      title="Record your verification video"
      subtitle="A short video showing your face and your photo ID together. No live call — just a quick 5-15 second recording."
    >
      <div className="rounded-xl border border-sky-200 bg-sky-50 p-3 mb-4 text-xs text-sky-900 space-y-1.5">
        <p className="font-semibold flex items-center gap-1.5"><Camera className="w-3.5 h-3.5" /> Please:</p>
        <ul className="list-disc pl-5 space-y-0.5">
          <li>Look directly at the camera</li>
          <li>Hold your driver licence next to your face so both are clearly visible</li>
          <li>Make sure you&apos;re in a well-lit area and the ID text is readable</li>
          <li>Recording is at least {REC_MIN_SECONDS} seconds — max {REC_MAX_SECONDS} seconds</li>
        </ul>
      </div>

      {previewUrl ? (
        <>
          {/* Always-visible captured-file confirmation — proves the recording exists even
              if the inline <video> preview happens to fail on the customer's browser. */}
          <div className="rounded-xl border-2 border-emerald-300 bg-emerald-50 p-3 mb-3 flex items-start gap-2" data-testid="idv-video-captured-card">
            <CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" />
            <div className="min-w-0 flex-1">
              <p className="text-xs font-bold text-emerald-900">Video recording captured</p>
              <p className="text-[11px] text-emerald-800 mt-0.5">
                {previewBlob ? `${(previewBlob.size / (1024 * 1024)).toFixed(2)} MB` : ''}
                {previewBlob && previewBlob.type ? ` · ${previewBlob.type.split(';')[0]}` : ''}
                {' — tap Submit to send it to your broker.'}
              </p>
            </div>
          </div>

          {previewError ? (
            <div className="rounded-xl border border-amber-300 bg-amber-50 p-3 mb-3 text-xs text-amber-900" data-testid="idv-video-preview-error">
              <p className="font-semibold mb-1">Your browser can&apos;t play this recording inline</p>
              <p>This is a known quirk of some mobile browsers — your recording is still saved and will be sent to your broker perfectly. Just tap Submit below, or Re-record if you&apos;d like to try again.</p>
            </div>
          ) : (
            <div className="rounded-xl overflow-hidden bg-black relative">
              <video
                key={previewUrl}
                ref={previewRef}
                controls
                playsInline
                preload="metadata"
                className="w-full h-auto max-h-[60vh]"
                data-testid="idv-video-playback"
                onError={() => setPreviewError(true)}
              >
                <source src={previewUrl} type={previewBlob?.type || 'video/webm'} />
              </video>
            </div>
          )}
          <p className="text-[11px] text-slate-500 mt-2 text-center">Review your recording. If it looks clear, submit. Otherwise re-take.</p>
          <div className="mt-4 grid grid-cols-2 gap-2">
            <button type="button" onClick={retake} className="rounded-full border border-slate-300 hover:bg-slate-50 text-slate-800 text-sm font-semibold py-3">Re-record</button>
            <button
              type="button"
              onClick={submit}
              disabled={uploading}
              data-testid="idv-video-submit"
              className="rounded-full bg-[#15133A] hover:bg-[#1E1B4B] text-white text-sm font-semibold py-3 disabled:opacity-50"
            >
              {uploading ? (<><Loader2 className="w-4 h-4 animate-spin inline mr-1.5" /> Uploading…</>) : 'Submit video'}
            </button>
          </div>
        </>
      ) : !supported ? (
        <>
          {/* MediaRecorder unavailable — fall back to native camera picker. */}
          <div className="rounded-xl border border-amber-300 bg-amber-50 p-3 mb-3 text-xs text-amber-900">
            Your browser doesn&apos;t support in-page recording. Tap below to record with your phone&apos;s camera app instead.
          </div>
          <input
            ref={fallbackInputRef}
            type="file"
            accept="video/*"
            capture="user"
            className="hidden"
            onChange={handleFallbackFile}
            data-testid="idv-video-fallback-input"
          />
          <div className="grid grid-cols-2 gap-2">
            <button type="button" onClick={onBack} className="rounded-full border border-slate-300 hover:bg-slate-50 text-slate-800 text-sm font-semibold py-3">Back</button>
            <button
              type="button"
              onClick={() => fallbackInputRef.current?.click()}
              data-testid="idv-video-open-native-camera"
              className="rounded-full bg-[#15133A] hover:bg-[#1E1B4B] text-white text-sm font-semibold py-3"
            >
              <Camera className="w-4 h-4 inline mr-1.5" /> Open camera
            </button>
          </div>
        </>
      ) : (
        <>
          <div className="rounded-xl overflow-hidden bg-slate-900 aspect-[3/4] sm:aspect-video relative flex items-center justify-center">
            <video ref={videoRef} playsInline muted className={`w-full h-full object-cover ${permissionState === 'granted' ? '' : 'hidden'}`} data-testid="idv-video-preview" />
            {permissionState !== 'granted' && (
              <div className="text-center px-4 text-white">
                <Camera className="w-8 h-8 mx-auto mb-2 opacity-80" />
                <p className="text-sm font-semibold">{permissionState === 'denied' ? 'Camera permission was denied — please enable it in your browser and try again.' : 'Enable your camera to start.'}</p>
                <button
                  type="button"
                  onClick={startCamera}
                  data-testid="idv-video-enable-cam"
                  className="mt-3 rounded-full bg-white text-slate-900 text-sm font-semibold px-4 py-2 inline-flex items-center gap-1.5"
                >
                  <Camera className="w-4 h-4" /> {permissionState === 'granting' ? 'Requesting…' : 'Enable camera'}
                </button>
              </div>
            )}
            {isRecording && (
              <div className="absolute top-3 left-3 flex items-center gap-1.5 rounded-full bg-red-600 text-white text-[11px] font-bold px-2 py-1">
                <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" /> REC · {elapsed}s
              </div>
            )}
          </div>

          <div className="mt-4 grid grid-cols-2 gap-2">
            <button type="button" onClick={onBack} className="rounded-full border border-slate-300 hover:bg-slate-50 text-slate-800 text-sm font-semibold py-3">Back</button>
            {isRecording ? (
              <button
                type="button"
                onClick={stopRecording}
                disabled={elapsed < REC_MIN_SECONDS}
                data-testid="idv-video-stop"
                className="rounded-full bg-red-600 hover:bg-red-700 text-white text-sm font-semibold py-3 disabled:opacity-50"
              >
                {elapsed < REC_MIN_SECONDS ? `Hold on… (${REC_MIN_SECONDS - elapsed}s left)` : 'Stop & preview'}
              </button>
            ) : (
              <button
                type="button"
                onClick={startRecording}
                disabled={permissionState !== 'granted'}
                data-testid="idv-video-start"
                className="rounded-full bg-[#15133A] hover:bg-[#1E1B4B] text-white text-sm font-semibold py-3 disabled:opacity-40"
              >
                <Camera className="w-4 h-4 inline mr-1.5" /> Start recording
              </button>
            )}
          </div>
        </>
      )}
    </StepCard>
  );
};


const UploadStep = ({ title, subtitle, icon: Icon, fileType, uploaded, uploading, onUpload, capture, onNext, onBack, nextLabel }) => {
  const inputRef = useRef(null);
  const [confirmed, setConfirmed] = useState(false);
  const meta = DOC_META[fileType] || { label: title, checklist: [] };
  const locked = !confirmed && !uploaded;

  const openPicker = () => {
    if (locked) return;
    inputRef.current?.click();
  };

  return (
    <StepCard title={title} subtitle={subtitle}>
      {/* PAUSE + CONFIRM BANNER — user MUST read this before the upload buttons unlock */}
      <div className="rounded-2xl border-2 border-amber-300 bg-amber-50 p-3 sm:p-5 mb-4">
        <div className="flex items-start gap-2.5 sm:gap-3">
          <div className="flex-shrink-0 w-7 h-7 sm:w-10 sm:h-10 rounded-full bg-amber-500 text-white flex items-center justify-center font-bold text-sm sm:text-lg">!</div>
          <div className="flex-1 min-w-0">
            <p className="text-[10px] sm:text-[11px] uppercase tracking-wider text-amber-700 font-bold">Pause — check before you upload</p>
            <p className="mt-1 text-sm sm:text-xl font-extrabold text-slate-900 leading-tight">
              You are about to upload the {meta.label}.
            </p>
            <ul className="mt-2 sm:mt-3 space-y-1 sm:space-y-1.5">
              {meta.checklist.map((item) => (
                <li key={item} className="flex items-start gap-2 text-xs sm:text-sm text-slate-800 leading-snug">
                  <CheckCircle2 className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
            {!uploaded && (
              <label className="mt-3 sm:mt-4 flex items-start gap-2 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={confirmed}
                  onChange={(e) => setConfirmed(e.target.checked)}
                  data-testid={`idv-confirm-${fileType}`}
                  className="mt-0.5 h-4 w-4 rounded border-slate-400 text-amber-600 focus:ring-amber-500"
                />
                <span className="text-xs sm:text-sm font-semibold text-slate-900 leading-snug">
                  I understand — I am about to upload the {meta.label}, not any other document.
                </span>
              </label>
            )}
          </div>
        </div>
      </div>

      <div className={`rounded-2xl border-2 border-dashed p-6 text-center transition ${locked ? 'border-slate-200 bg-slate-100 opacity-60' : 'border-slate-300 bg-slate-50'}`}>
        {uploaded ? (
          <div className="flex flex-col items-center">
            <div className="w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center mb-2">
              <CheckCircle2 className="w-7 h-7 text-emerald-600" />
            </div>
            <p className="text-sm font-semibold text-slate-900">Uploaded successfully</p>
            <p className="text-xs text-slate-500 mt-1">You can replace it before submitting if needed.</p>
          </div>
        ) : (
          <div className="flex flex-col items-center">
            <div className="w-14 h-14 rounded-full bg-white border border-slate-200 flex items-center justify-center mb-2">
              <Icon className="w-6 h-6 text-slate-500" />
            </div>
            <p className="text-sm font-semibold text-slate-700">
              {locked ? 'Tick the confirmation above to unlock' : 'Choose a file or take a photo'}
            </p>
          </div>
        )}
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          capture={capture}
          className="hidden"
          data-testid={`idv-upload-${fileType}`}
          onChange={(e) => onUpload(e.target.files?.[0])}
        />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-4">
          <button
            type="button"
            onClick={openPicker}
            disabled={uploading || locked}
            data-testid={`idv-upload-btn-${fileType}`}
            className={`inline-flex items-center justify-center gap-2 rounded-full text-xs sm:text-sm font-semibold py-2.5 leading-tight ${
              (uploading || locked)
                ? 'bg-slate-200 text-slate-500 cursor-not-allowed'
                : 'bg-[#15133A] hover:bg-[#1E1B4B] text-white'
            }`}
          >
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />}
            {uploaded ? 'Replace' : 'Take / choose photo'}
          </button>
          <button
            type="button"
            onClick={openPicker}
            disabled={uploading || locked}
            className={`inline-flex items-center justify-center gap-2 rounded-full border text-xs sm:text-sm font-semibold py-2.5 leading-tight ${
              (uploading || locked)
                ? 'border-slate-200 bg-slate-100 text-slate-400 cursor-not-allowed'
                : 'border-slate-300 hover:bg-slate-100 text-slate-800'
            }`}
          >
            <Upload className="w-4 h-4" /> Upload from device
          </button>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2 sm:gap-3 mt-2">
        <button
          type="button"
          onClick={onBack}
          className="w-full rounded-full border border-slate-300 hover:bg-slate-50 text-slate-800 text-xs sm:text-sm font-semibold py-2.5 sm:py-3 leading-tight"
        >
          Back
        </button>
        <button
          type="button"
          onClick={onNext}
          disabled={!uploaded}
          data-testid={`idv-next-from-${fileType}`}
          className={`w-full inline-flex items-center justify-center gap-1.5 rounded-full text-xs sm:text-sm font-semibold py-2.5 sm:py-3 leading-tight ${
            !uploaded
              ? 'bg-slate-200 text-slate-500 cursor-not-allowed'
              : 'bg-[#15133A] hover:bg-[#1E1B4B] text-white idv-btn-nudge'
          }`}
        >
          {nextLabel}
        </button>
      </div>
    </StepCard>
  );
};

const ReviewList = ({ form, uploads, idType, signatureSavedAt }) => (
  <div className="space-y-2">
    <ReviewRow label="First name" value={form.first_name} />
    <ReviewRow label="Last name" value={form.last_name} />
    <ReviewRow label="Date of birth" value={form.date_of_birth ? fmtDate(form.date_of_birth) : ''} />
    <ReviewRow label="Address" value={form.address} />
    <ReviewRow label="Email" value={form.email} />
    {form.mobile && <ReviewRow label="Mobile" value={form.mobile} />}
    <ReviewRow label="Video recording" value={uploads.recorded_video?.uploaded ? 'Recorded ✓' : 'Missing'} tone={uploads.recorded_video?.uploaded ? 'ok' : 'error'} />
    {idType === 'passport' ? (
      <ReviewRow label="Passport" value={uploads.passport?.uploaded ? 'Uploaded ✓' : 'Missing'} tone={uploads.passport?.uploaded ? 'ok' : 'error'} />
    ) : (
      <>
        <ReviewRow label="Driver licence (front)" value={uploads.driver_licence?.uploaded ? 'Uploaded ✓' : 'Missing'} tone={uploads.driver_licence?.uploaded ? 'ok' : 'error'} />
        <ReviewRow label="Driver licence (back)" value={uploads.driver_licence_back?.uploaded ? 'Uploaded ✓' : 'Missing'} tone={uploads.driver_licence_back?.uploaded ? 'ok' : 'error'} />
      </>
    )}
    <ReviewRow label="Medicare card" value={uploads.medicare_card?.uploaded ? 'Uploaded ✓' : 'Missing'} tone={uploads.medicare_card?.uploaded ? 'ok' : 'error'} />
    <ReviewRow label="Signature" value={signatureSavedAt ? 'Captured ✓' : 'Missing'} tone={signatureSavedAt ? 'ok' : 'error'} />
  </div>
);

// helpers
const fullName = (form) => `${form.first_name || ''} ${form.last_name || ''}`.trim();
const idDocsList = (uploads, idType) => {
  const parts = [];
  if (idType === 'passport') parts.push('Passport');
  else {
    if (uploads.driver_licence?.uploaded) parts.push('Driver Licence (front)');
    if (uploads.driver_licence_back?.uploaded) parts.push('Driver Licence (back)');
  }
  if (uploads.medicare_card?.uploaded) parts.push('Medicare Card');
  if (uploads.recorded_video?.uploaded) parts.push('Verification video');
  return parts.join(', ') || 'the uploaded documents';
};

const ReviewRow = ({ label, value, tone }) => (
  <div className="flex items-start justify-between gap-3 py-2 border-b border-slate-100 last:border-0">
    <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500">{label}</span>
    <span className={`text-sm text-right break-words ${tone === 'ok' ? 'text-emerald-600 font-semibold' : tone === 'error' ? 'text-red-600 font-semibold' : 'text-slate-900'}`}>
      {value || <span className="text-slate-400 italic">Not provided</span>}
    </span>
  </div>
);

const DoneScreen = ({ reference }) => {
  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
      <div className="bg-white rounded-3xl border border-slate-200/80 shadow-[0_2px_16px_rgba(15,19,58,0.06)] p-6 sm:p-9 text-center">
        <div className="w-16 h-16 rounded-2xl bg-emerald-50 mx-auto flex items-center justify-center mb-5">
          <CheckCircle2 className="w-9 h-9 text-emerald-600" />
        </div>
        <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-emerald-600">Step 7 · Verification complete</p>
        <h2 className="text-2xl sm:text-3xl font-black text-slate-950 mt-1.5 tracking-tight font-[Outfit]" data-testid="idv-done-title">You&apos;re all done</h2>
        <p className="text-sm text-slate-600 mt-2 leading-relaxed max-w-md mx-auto">
          Thank you. Your broker will now review your video, ID documents and details, then confirm your verification is complete.
        </p>
        {reference && (
          <div className="mt-5 inline-flex items-center gap-2 rounded-full bg-slate-50 border border-slate-200 px-4 py-2">
            <span className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Reference</span>
            <span className="font-mono font-bold text-sm text-slate-800">{reference}</span>
          </div>
        )}
        <p className="text-[11px] text-slate-400 mt-6 inline-flex items-center gap-1.5 justify-center"><Lock className="w-3 h-3" /> Stored encrypted · only accessible by your broker</p>
      </div>
    </div>
  );
};

export default IDVerifyCustomerPortal;
